import { useMemo } from 'react'
import type { GroupBase } from 'react-select'
import { observer } from 'mobx-react'

import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import type { SelectOption } from 'Components/Select/Select.types'

import { useCountryList } from 'Hooks'

import {
  ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_ATT_NAME,
  ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_NAME,
} from 'Constants/organisationConfig'

import { useShippingLabelCountryList } from 'Portal/src/hooks/useShippingLabelCountryList/useShippingLabelCountryList'
import { useStore } from 'Portal/src/stores'

import type { CountrySelectProps } from './CountrySelect.types'

export const CountrySelect = observer(
  <
    Option extends SelectOption,
    IsMulti extends boolean,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    props: CountrySelectProps<Option, IsMulti, Group>,
  ) => {
    const { isShippingLabelList, disabled, ...rest } = props

    const { organisationStore } = useStore()
    const { organisationConfigList } = organisationStore

    const customCountyList = organisationConfigList
      ?.find(
        ({ name }) =>
          name === ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_NAME,
      )
      ?.attributes.find(
        ({ attributeName }) =>
          attributeName ===
          ORGANISATION_CONFIG_LIST_ALLOWED_SHIPPING_COUNTRIES_ATT_NAME,
      )

    const countryList = useCountryList()
    const shippingLabelCountryList = useShippingLabelCountryList()

    const countryOptions = useMemo(() => {
      if (isShippingLabelList) {
        return Object.keys(shippingLabelCountryList).map(isoCode => ({
          label: shippingLabelCountryList[isoCode],
          value: isoCode,
        }))
      }

      const availableCountryIsoCodes = customCountyList?.attributeValue
        ? Object.keys(countryList).filter(isoCode =>
            customCountyList.attributeValue.includes(isoCode),
          )
        : Object.keys(countryList)

      return availableCountryIsoCodes.map(isoCode => ({
        label: countryList[isoCode],
        value: isoCode,
      }))
    }, [
      countryList,
      customCountyList,
      isShippingLabelList,
      shippingLabelCountryList,
    ])

    return (
      <Field
        {...rest}
        selectProps={{
          isClearable: true,
          isDisabled: disabled,
          options: countryOptions,
        }}
        type={FieldTypeEnum.select}
      />
    )
  },
)
